import React, { useState, useEffect, useCallback } from "react";
import { LicenseContext } from "./LicenseContext";
import { getLicenseStatus } from "../api/licenseApi";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // re-check every 5 minutes

/**
 * LicenseProvider
 * Fetches GET /api/license/status on mount and every 5 minutes.
 * Provides license status to all child components via LicenseContext.
 */
export function LicenseProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getLicenseStatus();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err.message ?? "Failed to load license status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  const hasFeature = useCallback(
    (feature) => {
      if (!status) return true; // optimistic while loading
      // Match case- and separator-insensitively ('_' == '-') to mirror the backend
      // PolicySnapshot.HasFeature, so key spelling drift can't hide a licensed feature.
      const norm = (s) => String(s).trim().toLowerCase().replace(/_/g, "-");
      const want = norm(feature);
      return Array.isArray(status.features) &&
        status.features.some((f) => norm(f) === want);
    },
    [status]
  );

  const value = {
    status,
    loading,
    error,
    isOperational: status?.isOperational ?? true,
    mode: status?.mode ?? "Community",
    tier: status?.tier ?? "community",
    hasFeature,
    // Resolved as soon as the first /status call settles — including when it FAILS, because `loading`
    // is cleared in the finally. A failed status check therefore falls back to exactly today's
    // behaviour (hasFeature optimistic, gated fetches fire), rather than leaving the product dark.
    featuresResolved: !loading,
    refresh,
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
}
